import React, { useMemo, useState } from 'react';
import { sitesService, siteColour } from '../../_services/sites.service';

export default function CampaignBroadcastModal({ campaign, sites, activeSalonId, onClose }) {
    const targets = useMemo(() => sites.filter((s) => String(s.salonId) !== String(activeSalonId)), [sites, activeSalonId]);
    const [selected, setSelected] = useState(() => targets.map((s) => String(s.salonId)));
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [result, setResult] = useState(null);

    const toggle = (id) => setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
    const submit = async () => {
        if (!selected.length) return;
        setLoading(true); setError('');
        try {
            const response = await sitesService.broadcastCampaign(campaign.id, selected);
            setResult(response);
        } catch (e) {
            setError(e?.response?.data?.error?.responseMessage || 'Could not create the location drafts.');
        } finally { setLoading(false); }
    };

    // The server is idempotent per location: a copy already there comes back
    // with existing: true rather than as a second draft.
    const created = result ? (result.created || []).filter((r) => r.campaignId && !r.existing) : [];
    const already = result ? (result.created || []).filter((r) => r.campaignId && r.existing) : [];
    const skipped = result ? (result.created || []).filter((r) => r.skipped) : [];

    return (
        <div role="dialog" aria-modal="true" aria-label="Copy campaign to locations" style={{ position: 'fixed', inset: 0, zIndex: 12000, display: 'grid', placeItems: 'center', padding: 20, background: 'rgba(20,20,24,0.45)' }} onMouseDown={(e) => { if (e.target === e.currentTarget && !loading) onClose(); }}>
            <div style={{ width: 'min(480px, 100%)', maxHeight: '85vh', overflow: 'auto', borderRadius: 18, background: '#fff', boxShadow: '0 24px 80px rgba(0,0,0,.2)', padding: 24 }}>
                <h2 style={{ margin: '0 0 7px', fontSize: 21 }}>Copy to locations</h2>
                <p style={{ margin: '0 0 20px', color: '#68686F', fontSize: 14, lineHeight: 1.5 }}>
                    Beu creates a separate draft at each location, using that location’s clients, consent and message balance.
                </p>

                {result ? (
                    <div>
                        <div style={{ padding: 14, borderRadius: 12, background: '#EFFAF5', color: '#147A4B', fontWeight: 650 }}>
                            {created.length} {created.length === 1 ? 'draft' : 'drafts'} created
                        </div>
                        {already.length > 0 && <p style={{ color: '#68686F', fontSize: 13, margin: '12px 0 0' }}>{already.length} {already.length === 1 ? 'location already has' : 'locations already have'} this campaign, so nothing new was made there.</p>}
                        {skipped.length > 0 && <p style={{ color: '#9A5B13', fontSize: 13, margin: '12px 0 0' }}>{skipped.length} skipped because your account is not attached to that location.</p>}
                        <button type="button" className="beu-btn-pill" onClick={onClose} style={{ width: '100%', marginTop: 20 }}>Done</button>
                    </div>
                ) : (
                    <>
                        <div style={{ display: 'grid', gap: 8 }}>
                            {targets.map((site) => {
                                const id = String(site.salonId);
                                return (
                                    <label key={id} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '12px 13px', border: '1px solid #E7E7EB', borderRadius: 11, cursor: 'pointer' }}>
                                        <input type="checkbox" checked={selected.includes(id)} onChange={() => toggle(id)} />
                                        <span aria-hidden style={{ width: 10, height: 10, borderRadius: '50%', background: siteColour(id) }} />
                                        <span style={{ fontSize: 14, fontWeight: 650 }}>{site.salonName}</span>
                                    </label>
                                );
                            })}
                        </div>
                        {error && <p style={{ margin: '12px 0 0', color: '#B14438', fontSize: 13 }}>{error}</p>}
                        <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                            <button type="button" onClick={onClose} disabled={loading} style={{ flex: 1, padding: '11px 18px', border: '1px solid #DEDEE2', borderRadius: 999, background: '#fff', fontWeight: 650 }}>Cancel</button>
                            <button type="button" className="beu-btn-pill" onClick={submit} disabled={loading || !selected.length} style={{ flex: 1 }}>
                                {loading ? 'Creating…' : `Create ${selected.length} ${selected.length === 1 ? 'draft' : 'drafts'}`}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
